import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Download, FileText, Copy, ExternalLink, RefreshCw, Share2, Calculator, Info, Sparkles, Users, Coins } from "lucide-react";
import InvestorROI from "@/pages/admin/InvestorROI";

function num(v, fallback = 0) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

// Australian rooftop-solar capacity factor by state (kWh generated per kW installed per year)
const STATE_KWH_PER_KW = {
  NSW: 1500, VIC: 1400, QLD: 1600, SA: 1600, WA: 1600, TAS: 1250, ACT: 1500, NT: 1700,
};
const DEFAULT_CF = 1500;
// Self-consumption fraction — what % of generated solar the site actually uses on-site
const SELF_CONSUMPTION_WITH_BATTERY = 85;   // %
const SELF_CONSUMPTION_SOLAR_ONLY = 40;     // %

export default function AdminProposal() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const pid = params.get("pid");
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [existingProposalId, setExistingProposalId] = useState(pid || null);
  const [form, setForm] = useState({
    business_name: "",
    site_address: "",
    current_annual_kwh: "",
    current_avg_rate_cents: "",
    current_annual_cost: "",
    system_size_kw: "",
    battery_size_kwh: "",
    annual_generation_kwh: "",
    estimated_energy_supplied_kwh: "",
    proposed_ppa_rate_cents: "",
    proposed_term_years: 10,
    proposed_annual_cost: "",
    estimated_annual_savings: "",
    estimated_co2_reduction_tonnes: "",
    assumptions: "",
    proposal_status: "draft",
    escalation_grid_pct: 5.0,
    escalation_ppa_pct: 3.0,
    degradation_pct_per_year: 0.5,
    projection_years: 10,
    share_token: "",
    // Investor / IRR
    capital_cost: "",
    annual_maintenance_cost: "",
    maintenance_escalation_pct: 3.0,
    discount_rate_pct: 10.0,
    terminal_value_aud: 0,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data: a } = await api.get(`/api/admin/applications/${id}`);
        setApp(a);
        const d = a.data || {};
        // Prefill from application if no existing proposal id
        if (!existingProposalId) {
          setForm((f) => ({
            ...f,
            business_name: d.legal_business_name || "",
            site_address: `${d.site_address || ""} ${d.site_suburb || ""} ${d.site_state || ""} ${d.site_postcode || ""}`.trim(),
            current_annual_kwh: d.annual_kwh || "",
            system_size_kw: a.estimated_system_kw || "",
            battery_size_kwh: a.estimated_battery_kwh || "",
            proposed_ppa_rate_cents: a.proposed_ppa_rate_cents || "",
            proposed_term_years: a.proposed_term_years || 10,
            estimated_annual_savings: a.estimated_annual_savings || "",
          }));
        } else {
          const { data: p } = await api.get(`/api/admin/proposals/${existingProposalId}`);
          setForm((f) => ({ ...f, ...p }));
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  // ---- Auto-compute chain (single unified effect) ----
  // Track which "derived" fields the admin has manually touched so we don't overwrite them.
  const [touched, setTouched] = useState({});
  const [autoAssumptions, setAutoAssumptions] = useState({
    capacity_factor: DEFAULT_CF,
    self_consumption_pct: SELF_CONSUMPTION_WITH_BATTERY,
  });
  const [showAssumptions, setShowAssumptions] = useState(false);

  // When we load the application, prefill capacity factor from site state
  const [assumpState, setAssumpState] = useState("NSW");
  useEffect(() => {
    if (!app) return;
    const st = ((app.data || {}).site_state || "").toUpperCase();
    if (STATE_KWH_PER_KW[st]) {
      setAssumpState(st);
      setAutoAssumptions((a) => ({ ...a, capacity_factor: STATE_KWH_PER_KW[st] }));
    }
  }, [app]);

  // If battery is added/removed, adjust the default self-consumption (unless the admin has customised it)
  const [selfConsumptionOverridden, setSelfConsumptionOverridden] = useState(false);
  useEffect(() => {
    if (selfConsumptionOverridden) return;
    const hasBattery = num(form.battery_size_kwh) > 0;
    setAutoAssumptions((a) => ({
      ...a,
      self_consumption_pct: hasBattery ? SELF_CONSUMPTION_WITH_BATTERY : SELF_CONSUMPTION_SOLAR_ONLY,
    }));
  }, [form.battery_size_kwh, selfConsumptionOverridden]);

  // Recompute all derived numbers whenever core inputs change (skips touched fields)
  useEffect(() => {
    const kwh = num(form.current_annual_kwh);
    const gridRate = num(form.current_avg_rate_cents);
    const sysKw = num(form.system_size_kw);
    const ppaRate = num(form.proposed_ppa_rate_cents);
    const cf = num(autoAssumptions.capacity_factor, DEFAULT_CF);
    const selfPct = num(autoAssumptions.self_consumption_pct, SELF_CONSUMPTION_WITH_BATTERY) / 100;

    const patch = {};

    // 1) Grid annual cost
    const annualCost = kwh * gridRate / 100;
    if (!touched.current_annual_cost && annualCost > 0) patch.current_annual_cost = Math.round(annualCost);

    // 2) Annual generation
    const gen = sysKw * cf;
    if (!touched.annual_generation_kwh && gen > 0) patch.annual_generation_kwh = Math.round(gen);

    // 3) Energy supplied from the system (cap at annual usage)
    const supplied = Math.min(gen * selfPct, kwh || Infinity);
    if (!touched.estimated_energy_supplied_kwh && supplied > 0) patch.estimated_energy_supplied_kwh = Math.round(supplied);

    // Use the freshly-computed (or existing) values for downstream calcs
    const suppliedFinal = touched.estimated_energy_supplied_kwh
      ? num(form.estimated_energy_supplied_kwh)
      : Math.round(supplied);

    // 4) Proposed annual cost = supplied × PPA + residual grid at grid rate
    const residualGridKwh = Math.max(kwh - suppliedFinal, 0);
    const newAnnualCost = suppliedFinal * ppaRate / 100 + residualGridKwh * gridRate / 100;
    if (!touched.proposed_annual_cost && newAnnualCost > 0) patch.proposed_annual_cost = Math.round(newAnnualCost);

    // 5) Est annual savings
    const savings = annualCost - newAnnualCost;
    if (!touched.estimated_annual_savings && annualCost > 0 && Number.isFinite(savings)) {
      patch.estimated_annual_savings = Math.round(savings);
    }

    // 6) CO2 reduction (t/yr)
    const co2 = suppliedFinal * 0.7 / 1000;
    if (!touched.estimated_co2_reduction_tonnes && co2 > 0) patch.estimated_co2_reduction_tonnes = co2.toFixed(1);

    if (Object.keys(patch).length > 0) setForm((f) => ({ ...f, ...patch }));
    // eslint-disable-next-line
  }, [
    form.current_annual_kwh, form.current_avg_rate_cents, form.system_size_kw,
    form.battery_size_kwh, form.proposed_ppa_rate_cents,
    autoAssumptions.capacity_factor, autoAssumptions.self_consumption_pct,
    touched.current_annual_cost, touched.annual_generation_kwh,
    touched.estimated_energy_supplied_kwh, touched.proposed_annual_cost,
    touched.estimated_annual_savings, touched.estimated_co2_reduction_tonnes,
  ]);

  function markTouched(field) {
    setTouched((t) => ({ ...t, [field]: true }));
  }
  function recomputeAll() {
    setTouched({});
    toast.success("Recomputed from inputs");
  }

  async function save() {
    setSaving(true);
    try {
      const body = {
        application_id: id,
        business_name: form.business_name,
        site_address: form.site_address,
        current_annual_kwh: num(form.current_annual_kwh),
        current_avg_rate_cents: num(form.current_avg_rate_cents),
        current_annual_cost: num(form.current_annual_cost),
        system_size_kw: num(form.system_size_kw),
        battery_size_kwh: num(form.battery_size_kwh),
        annual_generation_kwh: num(form.annual_generation_kwh),
        estimated_energy_supplied_kwh: num(form.estimated_energy_supplied_kwh),
        proposed_ppa_rate_cents: num(form.proposed_ppa_rate_cents),
        proposed_term_years: parseInt(form.proposed_term_years || 10, 10),
        proposed_annual_cost: num(form.proposed_annual_cost),
        estimated_annual_savings: num(form.estimated_annual_savings),
        estimated_co2_reduction_tonnes: num(form.estimated_co2_reduction_tonnes),
        assumptions: form.assumptions || "",
        escalation_grid_pct: num(form.escalation_grid_pct, 5.0),
        escalation_ppa_pct: num(form.escalation_ppa_pct, 3.0),
        degradation_pct_per_year: num(form.degradation_pct_per_year, 0.5),
        projection_years: parseInt(form.projection_years || 10, 10),
        capital_cost: num(form.capital_cost, 0),
        annual_maintenance_cost: num(form.annual_maintenance_cost, 0),
        maintenance_escalation_pct: num(form.maintenance_escalation_pct, 3.0),
        discount_rate_pct: num(form.discount_rate_pct, 10.0),
        terminal_value_aud: num(form.terminal_value_aud, 0),
      };
      let created;
      if (existingProposalId) {
        const { data } = await api.patch(`/api/admin/proposals/${existingProposalId}`, body);
        created = data;
      } else {
        const { data } = await api.post(`/api/admin/proposals`, body);
        setExistingProposalId(data.id);
        created = data;
      }
      setForm((f) => ({ ...f, ...created }));
      toast.success("Proposal saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function markSent() {
    if (!existingProposalId) return toast.error("Save the proposal first");
    try {
      const { data } = await api.patch(`/api/admin/proposals/${existingProposalId}`, { proposal_status: "sent" });
      setForm((f) => ({ ...f, ...data }));
      toast.success("Marked as sent");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  async function rotateToken() {
    if (!existingProposalId) return toast.error("Save the proposal first");
    if (!window.confirm("Rotate the share link? The current link will stop working immediately.")) return;
    try {
      const { data } = await api.post(`/api/admin/proposals/${existingProposalId}/rotate-token`);
      setForm((f) => ({ ...f, share_token: data.share_token }));
      toast.success("Share link rotated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  function copyShare() {
    if (!form.share_token) return;
    const url = `${window.location.origin}/proposal/${form.share_token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed"),
    );
  }

  const shareUrl = form.share_token ? `${window.location.origin}/proposal/${form.share_token}` : "";
  const pdfHref = existingProposalId ? `${API_BASE}/admin/proposals/${existingProposalId}/pdf` : null;

  // View tab: customer proposal inputs OR investor ROI
  const [view, setView] = useState("customer");

  return (
    <div data-testid="admin-proposal">
      <Link to={`/admin/applications/${id}`} className="inline-flex items-center gap-2 text-forest text-[13.5px] font-semibold mb-4">
        <ArrowLeft size={14}/> Back to application
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-2">Proposal builder</p>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-forest">Indicative business energy proposal</h1>
          <p className="text-neutral-500 text-[13.5px] mt-1">Application {app?.application_number} · {app?.data?.legal_business_name}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} data-testid="save-proposal" className="rounded-full h-11 px-5 bg-forest hover:bg-forest/90 text-white font-semibold">
            {saving ? "Saving..." : (existingProposalId ? "Update proposal" : "Save draft")}
          </Button>
          {pdfHref && (
            <a href={pdfHref} target="_blank" rel="noreferrer" data-testid="download-pdf">
              <Button variant="outline" className="rounded-full h-11 px-5 border-forest/20 text-forest hover:bg-mist font-semibold">
                <Download size={16} className="mr-1"/> Download PDF
              </Button>
            </a>
          )}
          {existingProposalId && form.proposal_status !== "sent" && (
            <Button onClick={markSent} data-testid="mark-sent" variant="outline" className="rounded-full h-11 px-5 border-bright text-forest bg-bright/10 font-semibold">
              Mark as sent
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4 inline-flex items-center gap-1 rounded-full bg-white border border-black/5 p-1" data-testid="proposal-tabs">
        <button
          type="button"
          onClick={() => setView("customer")}
          data-testid="tab-customer"
          className={`inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[13px] font-semibold transition-colors ${view === "customer" ? "bg-forest text-white" : "text-neutral-600 hover:text-forest"}`}
        >
          <Users size={14}/> Customer proposal
        </button>
        <button
          type="button"
          onClick={() => setView("investor")}
          data-testid="tab-investor"
          className={`inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[13px] font-semibold transition-colors ${view === "investor" ? "bg-forest text-white" : "text-neutral-600 hover:text-forest"}`}
        >
          <Coins size={14}/> Investor ROI
        </button>
      </div>

      {view === "investor" ? (
        <InvestorROI form={form} setForm={setForm} />
      ) : (
      <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
        {/* Inputs */}
        <section className="rounded-2xl bg-white border border-black/5 p-6 md:p-8 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-heading text-lg font-semibold text-forest">Inputs</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowAssumptions((s) => !s)}
                data-testid="toggle-sizing-assumptions"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-forest/80 hover:text-forest rounded-full border border-black/10 h-8 px-3"
              >
                <Info size={13}/> Sizing assumptions
              </button>
              <Button
                type="button"
                onClick={recomputeAll}
                data-testid="recompute-all"
                variant="outline"
                className="rounded-full h-8 px-3 border-bright text-forest bg-bright/10 hover:bg-bright/20 text-[12.5px] font-semibold"
              >
                <Calculator size={13} className="mr-1"/> Recalculate all
              </Button>
            </div>
          </div>

          <div className="rounded-xl bg-mist/70 border border-black/5 p-4 flex items-start gap-3" data-testid="autofill-help">
            <Sparkles size={16} className="text-bright mt-0.5"/>
            <div className="text-[12.5px] text-neutral-600 leading-relaxed">
              Enter <strong>annual kWh</strong>, <strong>grid rate</strong>, <strong>system size</strong>, <strong>battery size</strong> and <strong>PPA rate</strong> — everything else is computed automatically. Any field you edit becomes sticky; click <em>Recalculate all</em> to re-derive from inputs.
            </div>
          </div>

          {showAssumptions && (
            <div className="rounded-xl bg-white border border-forest/15 p-4" data-testid="sizing-assumptions-panel">
              <p className="text-[12.5px] font-semibold text-forest mb-3">Sizing assumptions used by the auto-calculator</p>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="State">
                  <select
                    value={assumpState}
                    onChange={(e) => {
                      const st = e.target.value;
                      setAssumpState(st);
                      if (STATE_KWH_PER_KW[st]) setAutoAssumptions((a) => ({ ...a, capacity_factor: STATE_KWH_PER_KW[st] }));
                    }}
                    data-testid="assump-state"
                    className="w-full h-10 rounded-md border border-input px-2 text-[13.5px] bg-white text-forest"
                  >
                    {Object.keys(STATE_KWH_PER_KW).map((s) => (
                      <option key={s} value={s}>{`${s} — ${STATE_KWH_PER_KW[s]} kWh/kW`}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Capacity factor (kWh / kW / yr)">
                  <Input
                    type="number"
                    value={autoAssumptions.capacity_factor}
                    onChange={(e) => setAutoAssumptions((a) => ({ ...a, capacity_factor: e.target.value }))}
                    data-testid="assump-cf"
                  />
                </Field>
                <Field label="Self-consumption % (of generation)">
                  <Input
                    type="number"
                    value={autoAssumptions.self_consumption_pct}
                    onChange={(e) => { setAutoAssumptions((a) => ({ ...a, self_consumption_pct: e.target.value })); setSelfConsumptionOverridden(true); }}
                    data-testid="assump-self"
                  />
                </Field>
              </div>
              <p className="mt-3 text-[11.5px] text-neutral-500">
                Defaults: <strong>85%</strong> self-consumption with battery, <strong>40%</strong> without. Capacity factor auto-adjusts to site state (NSW 1500 · VIC 1400 · QLD/SA/WA 1600 · TAS 1250 · ACT 1500 · NT 1700).
              </p>
            </div>
          )}
          {existingProposalId && shareUrl && (
            <div className="rounded-2xl bg-forest-gradient text-white p-5 md:p-6 relative overflow-hidden grain border border-white/10" data-testid="share-link-card">
              <div className="relative z-10">
                <div className="flex items-center gap-2 text-bright text-[11.5px] tracking-widest uppercase font-semibold">
                  <Share2 size={14}/> Customer share link
                </div>
                <p className="mt-2 text-white/75 text-[13.5px]">Send this link to the customer — they can view an interactive proposal (no login required).</p>
                <div className="mt-4 flex flex-col md:flex-row gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    data-testid="share-url-input"
                    className="flex-1 h-11 rounded-lg bg-white/10 border border-white/15 text-white px-3 text-[13.5px] font-mono truncate"
                    onFocus={(e) => e.target.select()}
                  />
                  <div className="flex gap-2">
                    <Button type="button" onClick={copyShare} data-testid="share-copy" className="rounded-lg h-11 px-4 bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold">
                      <Copy size={14} className="mr-1.5"/> Copy
                    </Button>
                    <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="share-open">
                      <Button type="button" variant="outline" className="rounded-lg h-11 px-4 border-white/30 bg-white/5 text-white hover:bg-white/10 font-semibold">
                        <ExternalLink size={14} className="mr-1.5"/> Open
                      </Button>
                    </a>
                    <Button type="button" onClick={rotateToken} data-testid="share-rotate" variant="outline" className="rounded-lg h-11 px-3 border-white/30 bg-white/5 text-white hover:bg-white/10">
                      <RefreshCw size={14}/>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Business name"><Input value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} data-testid="p-business-name" /></Field>
            <Field label="Site address"><Input value={form.site_address} onChange={(e) => setForm({...form, site_address: e.target.value})} data-testid="p-site-address" /></Field>
          </div>
          <p className="text-[13px] font-semibold text-forest">Current electricity profile</p>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Annual usage (kWh)"><Input type="number" value={form.current_annual_kwh} onChange={(e) => setForm({...form, current_annual_kwh: e.target.value})} data-testid="p-annual-kwh" /></Field>
            <Field label="Avg rate (c/kWh)"><Input type="number" value={form.current_avg_rate_cents} onChange={(e) => setForm({...form, current_avg_rate_cents: e.target.value})} data-testid="p-avg-rate" /></Field>
            <Field label="Annual cost (AUD)" auto={!touched.current_annual_cost}><Input type="number" value={form.current_annual_cost} onChange={(e) => { setForm({...form, current_annual_cost: e.target.value}); markTouched("current_annual_cost"); }} data-testid="p-annual-cost" /></Field>
          </div>
          <p className="text-[13px] font-semibold text-forest">Proposed solar & battery</p>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="System size (kW)"><Input type="number" value={form.system_size_kw} onChange={(e) => setForm({...form, system_size_kw: e.target.value})} data-testid="p-system-kw" /></Field>
            <Field label="Battery size (kWh)"><Input type="number" value={form.battery_size_kwh} onChange={(e) => setForm({...form, battery_size_kwh: e.target.value})} data-testid="p-battery-kwh" /></Field>
            <Field label="Annual generation (kWh)" auto={!touched.annual_generation_kwh}><Input type="number" value={form.annual_generation_kwh} onChange={(e) => { setForm({...form, annual_generation_kwh: e.target.value}); markTouched("annual_generation_kwh"); }} data-testid="p-annual-gen" /></Field>
            <Field label="Energy supplied from system (kWh)" auto={!touched.estimated_energy_supplied_kwh}><Input type="number" value={form.estimated_energy_supplied_kwh} onChange={(e) => { setForm({...form, estimated_energy_supplied_kwh: e.target.value}); markTouched("estimated_energy_supplied_kwh"); }} data-testid="p-supplied" /></Field>
          </div>
          <p className="text-[13px] font-semibold text-forest">PPA terms</p>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="PPA rate (c/kWh)"><Input type="number" value={form.proposed_ppa_rate_cents} onChange={(e) => setForm({...form, proposed_ppa_rate_cents: e.target.value})} data-testid="p-ppa-rate" /></Field>
            <Field label="Term (years)"><Input type="number" value={form.proposed_term_years} onChange={(e) => setForm({...form, proposed_term_years: e.target.value})} data-testid="p-term" /></Field>
            <Field label="Proposed annual cost (AUD)" auto={!touched.proposed_annual_cost}><Input type="number" value={form.proposed_annual_cost} onChange={(e) => { setForm({...form, proposed_annual_cost: e.target.value}); markTouched("proposed_annual_cost"); }} data-testid="p-proposed-cost" /></Field>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Est. annual savings (AUD)" auto={!touched.estimated_annual_savings}><Input type="number" value={form.estimated_annual_savings} onChange={(e) => { setForm({...form, estimated_annual_savings: e.target.value}); markTouched("estimated_annual_savings"); }} data-testid="p-savings" /></Field>
            <Field label="Est. CO₂ reduction (t/yr)" auto={!touched.estimated_co2_reduction_tonnes}><Input type="number" value={form.estimated_co2_reduction_tonnes} onChange={(e) => { setForm({...form, estimated_co2_reduction_tonnes: e.target.value}); markTouched("estimated_co2_reduction_tonnes"); }} data-testid="p-co2" /></Field>
          </div>
          <Field label="Assumptions & notes">
            <Textarea rows={5} value={form.assumptions} onChange={(e) => setForm({...form, assumptions: e.target.value})} data-testid="p-assumptions" placeholder="Key assumptions, network requirements, engineering notes..." />
          </Field>
          <p className="text-[13px] font-semibold text-forest">Projection assumptions (used in the customer web view)</p>
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Grid escalation % / yr"><Input type="number" step="0.1" value={form.escalation_grid_pct} onChange={(e) => setForm({...form, escalation_grid_pct: e.target.value})} data-testid="p-grid-esc" /></Field>
            <Field label="PPA escalation % / yr"><Input type="number" step="0.1" value={form.escalation_ppa_pct} onChange={(e) => setForm({...form, escalation_ppa_pct: e.target.value})} data-testid="p-ppa-esc" /></Field>
            <Field label="System degradation % / yr"><Input type="number" step="0.1" value={form.degradation_pct_per_year} onChange={(e) => setForm({...form, degradation_pct_per_year: e.target.value})} data-testid="p-degrade" /></Field>
            <Field label="Projection horizon (yrs)"><Input type="number" value={form.projection_years} onChange={(e) => setForm({...form, projection_years: e.target.value})} data-testid="p-horizon" /></Field>
          </div>
        </section>

        {/* Preview */}
        <aside className="rounded-2xl bg-forest text-white p-6 md:p-8 sticky top-6 h-fit grain">
          <p className="text-[11px] tracking-widest uppercase text-bright font-semibold">Indicative preview</p>
          <h3 className="mt-2 font-heading text-2xl font-semibold">{form.business_name || "Business name"}</h3>
          <p className="text-white/70 text-[13.5px]">{form.site_address || "Site address"}</p>

          <div className="mt-6 rounded-xl bg-white/[0.06] border border-white/10 p-4">
            <p className="text-[11px] uppercase tracking-widest text-white/60">Est. annual savings</p>
            <p className="font-heading text-3xl font-semibold text-bright mt-1">
              {form.estimated_annual_savings ? `$${Number(form.estimated_annual_savings).toLocaleString()}` : "—"}
            </p>
            <p className="text-[11.5px] text-white/50 mt-1">Indicative only. Not guaranteed.</p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
            <PreviewKV label="System" value={form.system_size_kw ? `${form.system_size_kw} kW` : "—"} />
            <PreviewKV label="Battery" value={form.battery_size_kwh ? `${form.battery_size_kwh} kWh` : "—"} />
            <PreviewKV label="PPA rate" value={form.proposed_ppa_rate_cents ? `${form.proposed_ppa_rate_cents} c/kWh` : "—"} />
            <PreviewKV label="Term" value={form.proposed_term_years ? `${form.proposed_term_years} yrs` : "—"} />
            <PreviewKV label="Grid annual cost" value={form.current_annual_cost ? `$${Number(form.current_annual_cost).toLocaleString()}` : "—"} />
            <PreviewKV label="PPA annual cost" value={form.proposed_annual_cost ? `$${Number(form.proposed_annual_cost).toLocaleString()}` : "—"} />
          </div>

          <p className="mt-5 text-[11.5px] text-white/60 leading-relaxed">
            Disclaimer — Indicative only. Subject to detailed site inspection, engineering design,
            network approval, credit approval, funding approval and execution of a formal Power
            Purchase Agreement.
          </p>

          {pdfHref && (
            <a href={pdfHref} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-bright font-semibold">
              <FileText size={16}/> Open latest PDF
            </a>
          )}
        </aside>
      </div>
      )}
    </div>
  );
}

function Field({ label, auto, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-forest flex items-center gap-1.5">
        {label}
        {auto && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-bright/15 text-forest text-[10px] font-semibold px-1.5 py-0.5 uppercase tracking-widest">
            auto
          </span>
        )}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function PreviewKV({ label, value }) {
  return (
    <div className="bg-white/[0.05] border border-white/10 rounded-lg p-3">
      <p className="text-[10.5px] uppercase tracking-widest text-white/50">{label}</p>
      <p className="mt-1 font-semibold text-white">{value}</p>
    </div>
  );
}
